import { useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../msalConfig';
import { msalConfig } from '../msalConfig';
import { getInitials } from '../utils/userUtils';
import { ADMIN_ROLE, ADMIN_GROUP_ID, ADMIN_CUSTOM_CLAIM } from '../config/adminClaims';

const DISABLE_MSAL = process.env.REACT_APP_DISABLE_MSAL === 'true'; // changed (was true)

function useAuthMock() {
  return {
    isAuthenticated: true,
    userInfo: {
      name: 'Test User',          // mock only if disabled
      email: 'test.user@example.com',
      initials: 'TU'
    },
    isAdmin: false,               // changed (was true)
    authLoading: false,
    inProgress: null,
    handleSignIn: () => {},
    handleSignOut: () => {}
  };
}

function useAuthReal() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [userInfo, setUserInfo] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      const claims = account.idTokenClaims || {};

      const emailCandidate = (
        claims.email ||
        claims.preferred_username ||
        claims.upn ||
        account.username ||
        ''
      ).toLowerCase();

      const displayName = account.name || claims.name || emailCandidate;

      const user = {
        name: displayName,
        email: emailCandidate,
        initials: getInitials(displayName),
        accountId: account.localAccountId,
        tenantId: account.tenantId
      };
      setUserInfo(user);

      const roles = Array.isArray(claims.roles) ? claims.roles : [];
      const groups = Array.isArray(claims.groups) ? claims.groups : [];
      const customClaimValue = ADMIN_CUSTOM_CLAIM ? claims[ADMIN_CUSTOM_CLAIM] : undefined;

      const hasRole = ADMIN_ROLE && roles.includes(ADMIN_ROLE);
      const inGroup = ADMIN_GROUP_ID && groups.includes(ADMIN_GROUP_ID);
      const hasCustom = ADMIN_CUSTOM_CLAIM && (customClaimValue === true || customClaimValue === 'true' || customClaimValue === '1');

      setIsAdmin(Boolean(hasRole || inGroup || hasCustom));

      console.log('User logged in (claims derived):', user);
      console.log('Admin resolution:', { roles, groups, customClaimValue });
    }
  }, [isAuthenticated, accounts]);

  const handleSignIn = async () => {
    try {
      setAuthLoading(true);
      const loginResponse = await instance.loginPopup(loginRequest);
      if (loginResponse.account) {
        console.log('Login successful for:', loginResponse.account.name);
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOut = async (clearChat) => {
    try {
      setAuthLoading(true);
      const logoutRequest = {
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
        mainWindowRedirectUri: msalConfig.auth.postLogoutRedirectUri
      };
      await instance.logoutPopup(logoutRequest);
      setUserInfo(null);
      setIsAdmin(false);
      if (typeof clearChat === 'function') clearChat();
      console.log('User signed out successfully');
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  return {
    isAuthenticated,
    userInfo,
    isAdmin,
    authLoading,
    inProgress,
    handleSignIn,
    handleSignOut
  };
}

export const useAuth = DISABLE_MSAL ? useAuthMock : useAuthReal; // unchanged logic but now dynamic
